import { getToken } from './storage';

// Capacidades del usuario derivadas de los claims del JWT emitido por IdentityServer.
export interface UserClaims {
  username: string | null;
  role: string | null;
  isAdmin: boolean;
  // ISO-2 de los países visibles (claim user-country-code).
  userCountries: string[];
  // ISO-2 de los países del NUEVO flujo donde el usuario es self-managed. Ya filtrado:
  // un self-managed en ES/AR (clásicos) NO aparece aquí (ver NEW_FLOW_COUNTRIES).
  selfManagedCountries: string[];
  // Alcance de visibilidad (claim view-scope).
  viewScope: 'all' | 'own' | null;
  // true si el usuario puede crear su propia planning en algún país.
  isSelfManaged: boolean;
  // Rol "Supplier" (2026-09-10): un proveedor que entra a rellenar SOLO las
  // cargas de sus proveedores. No crea plannings ni ve otras cargas.
  isSupplier: boolean;
  // Sus proveedores, tal y como los emite Identity: códigos de X3 (claim
  // supplier-code) y nombres libres (claim supplier-name). Solo informativos
  // aquí: el alcance de las cargas lo decide el servidor con el mismo token.
  supplierCodes: string[];
  supplierNames: string[];
}

const ADMIN_ROLE = 'admin@aldahra.com';
export const SUPPLIER_ROLE = 'Supplier';
const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
const NAME_CLAIM = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name';

// Países del NUEVO flujo (self-managed, con pantalla propia). España (ES) y Argentina (AR)
// son CLÁSICOS: un self-managed-country de ES/AR no cuenta como flujo nuevo. Esto evita que
// un admin marcado self-managed en ES quede atrapado en el flujo nuevo (create planning, etc.).
export const NEW_FLOW_COUNTRIES = ['PK', 'EG', 'BG', 'RO', 'GR', 'IT', 'XX'];

const EMPTY: UserClaims = {
  username: null,
  role: null,
  isAdmin: false,
  userCountries: [],
  selfManagedCountries: [],
  viewScope: null,
  isSelfManaged: false,
  isSupplier: false,
  supplierCodes: [],
  supplierNames: [],
};

// Un claim multi-valor viaja como array, pero si solo hay un valor el JWT lo
// colapsa a string. Normalizamos siempre a array de strings.
function toArray(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  if (typeof value === 'string') return [value];
  return [];
}

function decodePayload(token: string): Record<string, unknown> | null {
  try {
    return JSON.parse(atob(token.split('.')[1])) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function getClaims(): UserClaims {
  const token = getToken();
  if (!token) return EMPTY;
  const payload = decodePayload(token);
  if (!payload) return EMPTY;

  const role = typeof payload[ROLE_CLAIM] === 'string' ? (payload[ROLE_CLAIM] as string) : null;
  // Solo cuentan los self-managed-country del NUEVO flujo (PK/EG/BG/RO/GR/IT/XX). Un
  // self-managed en ES/AR (clásicos) se ignora → esos usuarios siguen como office/admin.
  const selfManagedCountries = toArray(payload['self-managed-country'])
    .filter((c) => NEW_FLOW_COUNTRIES.indexOf(c) >= 0);
  const viewScopeRaw = payload['view-scope'];
  const isSupplier = typeof role === 'string' && role.trim().toLowerCase() === SUPPLIER_ROLE.toLowerCase();

  return {
    username: typeof payload[NAME_CLAIM] === 'string' ? (payload[NAME_CLAIM] as string) : null,
    role,
    isAdmin: role === ADMIN_ROLE,
    userCountries: toArray(payload['user-country-code']),
    selfManagedCountries,
    viewScope: viewScopeRaw === 'all' || viewScopeRaw === 'own' ? viewScopeRaw : null,
    // Un proveedor nunca es self-managed aunque le hayan dado países: el
    // servidor también se lo niega, pero así no se le enseña ni el botón.
    isSelfManaged: !isSupplier && selfManagedCountries.length > 0,
    isSupplier,
    supplierCodes: toArray(payload['supplier-code']),
    supplierNames: toArray(payload['supplier-name']),
  };
}
